import React, { useState } from 'react';
import {
  Alert, Badge, Button, Card, Modal, Spinner,
} from 'react-bootstrap';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { socialMediaUrl, socialMediaDisplay } from '../utils/socialMedia';

/**
 * AIEnrichmentButton — opens a modal that runs the AI enrichment in two
 * steps:
 *   1. Calls /enrich-with-ai with apply=false to get a preview + the
 *      list of fields that would be overwritten.
 *   2. Shows a warning modal with the proposed values and explicit
 *      overwrite markers.
 *   3. User clicks "Apply" → POST again with apply=true → reloads the
 *      form data so the new values appear in the inputs.
 *
 * After a successful apply, surfaces an "Undo Last AI Enrichment" link
 * that pops the most recent history entry on the candidate doc.
 *
 * Props:
 *   candidateId      — required
 *   hasHistory       — boolean; whether a prior enrichment exists
 *                      (drives the visibility of the Undo button).
 *   onApplied()      — called after a successful apply; the parent
 *                      should re-fetch the candidate to refresh the
 *                      form fields.
 *   onUndone()       — called after a successful undo.
 */
export default function AIEnrichmentButton({
  candidateId,
  hasHistory = false,
  onApplied,
  onUndone,
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const handleOpen = async () => {
    setError('');
    setPreview(null);
    setOpen(true);
    setLoading(true);
    try {
      const res = await adminApi.post(
        `/admin/candidates/${candidateId}/enrich-with-ai`,
        { apply: false },
      );
      setPreview(res.data);
      const enrichedKeys = Object.keys(res.data?.enriched || {});
      if (enrichedKeys.length === 0) {
        setError('Claude could not find verified information for this candidate. Try populating the home state/county/race participation first, then re-run.');
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setError('');
    try {
      const res = await adminApi.post(
        `/admin/candidates/${candidateId}/enrich-with-ai`,
        { apply: true },
      );
      const n = Object.keys(res.data?.enriched || {}).length;
      toast.success(`Applied AI enrichment to ${n} field${n === 1 ? '' : 's'}.`);
      setOpen(false);
      if (onApplied) onApplied(res.data);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setApplying(false);
    }
  };

  const handleUndo = async () => {
    setUndoing(true);
    try {
      const res = await adminApi.post(
        `/admin/candidates/${candidateId}/undo-last-enrichment`,
      );
      if (res.data?.undone) {
        const fields = (res.data.restored_fields || []).join(', ');
        toast.success(`Restored fields: ${fields}`);
        if (onUndone) onUndone(res.data);
      } else {
        toast.info(res.data?.reason || 'Nothing to undo.');
      }
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setUndoing(false);
    }
  };

  const enriched = preview?.enriched || {};
  const overwrites = preview?.overwrites || [];
  const willOverwrite = overwrites.filter(o => o.has_existing);
  const newOnly = overwrites.filter(o => !o.has_existing);

  return (
    <>
      <div className="d-flex gap-2 flex-wrap">
        <Button
          variant="outline-info"
          size="sm"
          onClick={handleOpen}
          data-testid="ai-enrich-btn"
          title="Use Claude to research and propose updated profile data"
        >
          <i className="bi bi-stars me-1" />
          Enrich with AI
        </Button>
        {hasHistory && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleUndo}
            disabled={undoing}
            data-testid="ai-undo-btn"
            title="Restore field values from before the last AI enrichment"
          >
            {undoing && <Spinner size="sm" className="me-1" />}
            <i className="bi bi-arrow-counterclockwise me-1" />
            Undo Last AI Enrichment
          </Button>
        )}
      </div>

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        size="lg"
        backdrop="static"
        data-testid="ai-enrich-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-stars me-2 text-info" />
            Enrich Profile with AI
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className="text-center py-5" data-testid="ai-enrich-loading">
              <Spinner animation="border" variant="info" />
              <div className="mt-2 text-muted">
                Asking Claude to research this candidate&hellip;
              </div>
              <div className="mt-1 text-muted small">
                This typically takes 10–30 seconds.
              </div>
            </div>
          )}

          {error && !loading && (
            <Alert variant="warning" data-testid="ai-enrich-error">{error}</Alert>
          )}

          {!loading && preview && Object.keys(enriched).length > 0 && (
            <>
              <Alert variant="light" className="small mb-3">
                <i className="bi bi-info-circle me-1" />
                Claude proposes updates to <strong>{Object.keys(enriched).length}</strong> field{Object.keys(enriched).length === 1 ? '' : 's'}.
                {' '}Review the preview below, then click <strong>Apply</strong> to save.
                You can undo the change immediately after apply.
              </Alert>

              {willOverwrite.length > 0 && (
                <Alert variant="warning" data-testid="ai-enrich-overwrite-warning">
                  <strong>⚠️ Overwrite warning</strong>
                  <div className="mt-1">
                    The following fields already have data that will be replaced:
                  </div>
                  <div className="mt-1">
                    {willOverwrite.map(o => (
                      <Badge bg="dark" key={o.field} className="me-1 mb-1">{o.field}</Badge>
                    ))}
                  </div>
                </Alert>
              )}

              {newOnly.length > 0 && (
                <div className="mb-3">
                  <strong className="d-block mb-1 text-success small">New fields (no existing data)</strong>
                  <div>
                    {newOnly.map(o => (
                      <Badge bg="success" key={o.field} className="me-1 mb-1">{o.field}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3">
                <strong className="d-block mb-2">Preview of proposed values</strong>
                {overwrites.map((o) => (
                  <Card key={o.field} className="mb-2" data-testid={`ai-enrich-preview-${o.field}`}>
                    <Card.Header className="d-flex justify-content-between align-items-center py-1 px-2">
                      <small><code>{o.field}</code></small>
                      {o.has_existing
                        ? <Badge bg="warning" text="dark">Overwrites</Badge>
                        : <Badge bg="success">New</Badge>
                      }
                    </Card.Header>
                    <Card.Body className="py-2 px-2" style={{ fontSize: '0.85rem' }}>
                      <FieldPreview fieldName={o.field} value={o.after} />
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {preview.warnings?.length > 0 && (
                <Alert variant="info" className="mt-3 small">
                  <strong>Notes from the AI:</strong>
                  <ul className="mb-0 mt-1">
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setOpen(false)}
            disabled={applying}
            data-testid="ai-enrich-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="info"
            onClick={handleApply}
            disabled={applying || loading || !preview || Object.keys(enriched).length === 0}
            data-testid="ai-enrich-apply-btn"
          >
            {applying && <Spinner size="sm" className="me-1" />}
            <i className="bi bi-check-lg me-1" />
            {willOverwrite.length > 0
              ? `Apply (Overwrite ${willOverwrite.length})`
              : 'Apply'
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/**
 * Renders a single proposed value in the preview list. Branches on
 * field name AND shape to keep arrays/objects readable instead of
 * dumping JSON blobs.
 */
function FieldPreview({ fieldName, value }) {
  if (value == null || value === '') {
    return <span className="text-muted">(empty)</span>;
  }

  // Photo URL — render the actual image with a fallback message so the
  // admin can verify it's the right person before clicking Apply.
  if (fieldName === 'photo_url' && typeof value === 'string') {
    return (
      <div className="d-flex align-items-start gap-2">
        <img
          src={value}
          alt="Proposed candidate photo"
          style={{ maxWidth: 120, maxHeight: 160, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
        />
        <div style={{ display: 'none' }} className="text-warning small">
          ⚠️ Image failed to load — URL may be invalid or behind authentication.
        </div>
        <code className="small text-muted text-break" style={{ flex: 1 }}>{value}</code>
      </div>
    );
  }

  // Key issues — render each as title + description (markdown-ish text).
  if (fieldName === 'key_issues' && Array.isArray(value)) {
    return (
      <div>
        {value.map((issue, i) => (
          <div key={i} className="mb-2 pb-2 border-bottom">
            <strong>{issue.title || 'Issue'}</strong>
            {issue.description && (
              <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                {issue.description}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Endorsements — render org → description → link.
  if (fieldName === 'endorsements' && Array.isArray(value)) {
    return (
      <div>
        {value.map((e, i) => (
          <div key={i} className="mb-2 pb-2 border-bottom">
            <strong>{e.organization || 'Organization'}</strong>
            {e.description && (
              <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                {e.description}
              </div>
            )}
            {e.url && (
              <a href={e.url} target="_blank" rel="noreferrer" className="small">
                {e.url}
              </a>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Source links — render as bullet list of title + url.
  if (fieldName === 'source_links' && Array.isArray(value)) {
    return (
      <ul className="mb-0 ps-3">
        {value.map((s, i) => (
          <li key={i}>
            <strong>{s.title || 'Source'}</strong>{' — '}
            <a href={s.url} target="_blank" rel="noreferrer">{s.url}</a>
          </li>
        ))}
      </ul>
    );
  }

  // Social media — dictionary of platform → URL/handle. Render the
  // compact display (e.g., '@handle') with a clickable link to the
  // proper platform URL even when the stored value is just a handle.
  if (fieldName === 'social_media' && typeof value === 'object' && !Array.isArray(value)) {
    return (
      <ul className="mb-0 ps-3 list-unstyled">
        {Object.entries(value).map(([platform, raw]) => {
          const href = socialMediaUrl(platform, raw);
          const label = socialMediaDisplay(platform, raw) || String(raw);
          return (
            <li key={platform}>
              <strong>{platform}:</strong>{' '}
              {href
                ? <a href={href} target="_blank" rel="noreferrer" className="small">{label}</a>
                : <span className="text-muted small">{label}</span>}
            </li>
          );
        })}
      </ul>
    );
  }

  // Generic arrays / objects — readable but unstyled
  if (Array.isArray(value)) {
    return (
      <ul className="mb-0 ps-3">
        {value.map((item, i) => (
          <li key={i}>
            {typeof item === 'string' ? item : (
              <code style={{ fontSize: '0.75rem' }}>{JSON.stringify(item)}</code>
            )}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    return (
      <ul className="mb-0 ps-3 list-unstyled">
        {Object.entries(value).map(([k, v]) => (
          <li key={k}>
            <strong>{k}:</strong> <span className="text-muted">{String(v)}</span>
          </li>
        ))}
      </ul>
    );
  }
  // String — render with line breaks preserved
  return (
    <div style={{ whiteSpace: 'pre-wrap' }}>
      {String(value)}
    </div>
  );
}
